import { useState, useMemo } from "react";
import NumberFlow from "@number-flow/react";
import { useNavigate } from "react-router-dom";
import {
  Briefcase,
  Check,
  Globe,
  FileText,
  Activity,
  BarChart3,
  ExternalLink,
  Plus,
  Wallet,
} from "lucide-react";
import { cn } from "@/utils/Functions";
import { Tabs, TabsList, TabsTrigger, TabsPanel } from "@/components/ui/tabs";
import { Card, CardHeader, CardTitle, CardPanel } from "@/components/ui/card";
import { Button } from "@/components/ui/Button";
import { PATH_ROOT } from "@/routes/paths";
import { useIsDemo } from "@/hooks/common/useIsDemo";
import { useDealPipelineData } from "@/hooks/deals/useDealPipelineData";
import type { Deal, PipelineStage } from "@/types/deal.types";
import { usePortfolio } from "@/contexts/PortfolioContext";
import type { PortfolioEntity, Transaction, ActivityEntry } from "@/types/portfolio.types";
import { FinancialsTab } from "./financials";

const stageStyles: Record<PipelineStage, { label: string; color: string }> = {
  lead: { label: "Lead", color: "text-slate-500 bg-slate-500/10" },
  in_progress: { label: "In Progress", color: "text-blue-500 bg-blue-500/10" },
  signing: { label: "Signing", color: "text-purple-500 bg-purple-500/10" },
  delivery: { label: "Delivery", color: "text-amber-500 bg-amber-500/10" },
  complete: { label: "Complete", color: "text-emerald-500 bg-emerald-500/10" },
  won: { label: "Won", color: "text-emerald-500 bg-emerald-500/10" },
  lost: { label: "Lost", color: "text-red-500 bg-red-500/10" },
  closed: { label: "Closed", color: "text-amber-500 bg-amber-500/10" },
};

const STAGE_ORDER: PipelineStage[] = ["lead", "in_progress", "signing", "delivery", "complete"];

function getEntityDeals(entityId: string | null, dealMap: Record<string, string[]>, allDeals: Deal[]): Deal[] {
  if (!entityId) {
    return allDeals;
  }
  const dealIds = dealMap[entityId] ?? [];
  return allDeals.filter((d) => dealIds.includes(d.id));
}

function getEntityTransactions(entityId: string | null, txMap: Record<string, Transaction[]>): Transaction[] {
  if (!entityId) {
    return Object.values(txMap).flat();
  }
  return txMap[entityId] ?? [];
}

function getEntityActivity(entityId: string | null, actMap: Record<string, ActivityEntry[]>): ActivityEntry[] {
  if (!entityId) {
    return Object.values(actMap)
      .flat()
      .sort((a, b) => a.id.localeCompare(b.id));
  }
  return actMap[entityId] ?? [];
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function EntitySidebar({
  entities,
  activeId,
  onSelect,
}: {
  entities: PortfolioEntity[];
  activeId: string | null;
  onSelect: (id: string | null) => void;
}) {
  const { initialDeals } = useDealPipelineData();
  return (
    <div className="sticky top-0 w-[300px] shrink-0 border-r border-border overflow-y-auto">
      <div className="p-3 space-y-1">
        {/* All Portfolios option */}
        <button
          onClick={() => onSelect(null)}
          className={cn(
            "flex items-start gap-3 p-2.5 rounded-xl transition-all text-left w-full",
            activeId === null
              ? "bg-primary text-primary-foreground"
              : "hover:bg-muted/50"
          )}
        >
          <div
            className={cn(
              "size-8 rounded-full grid place-items-center shrink-0",
              activeId === null
                ? "text-primary-foreground"
                : "text-foreground/80 bg-white/20"
            )}
          >
            <Globe className="size-4" />
          </div>
          <div className="flex-1 min-w-0">
            <div className={cn("text-sm font-medium truncate", activeId === null ? "text-foreground" : "text-foreground/80")}>
              All Portfolios
            </div>
            <div className="text-xs text-foreground/80">
              {initialDeals.length} deals · Aggregate view
            </div>
          </div>
          {activeId === null && <Check className="size-4 shrink-0 text-primary" />}
        </button>

        {entities.map((entity) => {
          const isActive = activeId === entity.id;
          return (
            <button
              key={entity.id}
              onClick={() => onSelect(entity.id)}
              className={cn(
                "flex items-center gap-3 p-2.5 rounded-xl transition-all text-left w-full",
                isActive
                  ? `${entity.activeBg}`
                  : "hover:bg-muted/50"
              )}
            >
              <div
                className={cn(
                  "size-8 rounded-full grid place-items-center shrink-0 transition-colors overflow-hidden",
                  !entity.image && (isActive ? entity.color : "text-muted-foreground bg-muted/50")
                )}
              >
                {entity.image ? (
                  <img src={entity.image} alt="" className="size-full object-cover" />
                ) : (
                  <entity.icon className="size-4" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className={cn("text-sm font-medium truncate", isActive ? "text-foreground" : "text-foreground/80")}>
                  {entity.name}
                </div>
                <div className="text-xs text-foreground/60">
                  {entity.activeDeals} deal{entity.activeDeals !== 1 ? "s" : ""} · {entity.totalValue}
                </div>
              </div>
              <span className={cn(
                "text-[10px] font-medium px-2 py-0.5 rounded-full capitalize",
                isActive ? entity.color : "text-muted-foreground"
              )}>
                {entity.type}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function OverviewTab({
  deals,
  transactions,
  activity,
  entity,
}: {
  deals: Deal[];
  transactions: Transaction[];
  activity: ActivityEntry[];
  entity: PortfolioEntity | null;
}) {
  const totalValue = deals.reduce((sum, d) => sum + d.value, 0);
  const completedStages: PipelineStage[] = ["complete", "won", "closed"];
  const activeDeals = deals.filter((d) => !completedStages.includes(d.stage) && d.stage !== "lost").length;
  const completedDeals = deals.filter((d) => completedStages.includes(d.stage)).length;

  const stageCounts = useMemo(() => {
    const counts: Record<PipelineStage, number> = {
      lead: 0,
      in_progress: 0,
      signing: 0,
      delivery: 0,
      complete: 0,
      won: 0,
      lost: 0,
      closed: 0,
    };
    deals.forEach((d) => { counts[d.stage]++; });
    return counts;
  }, [deals]);

  const summaryCards = [
    { label: "Total Deals", value: deals.length, format: {} as Intl.NumberFormatOptions, icon: FileText },
    { label: "Total Value", value: totalValue, format: { style: "currency", currency: "USD", maximumFractionDigits: 0 } as Intl.NumberFormatOptions, icon: Wallet },
    { label: "Active Deals", value: activeDeals, format: {} as Intl.NumberFormatOptions, icon: BarChart3 },
    { label: "Completed", value: completedDeals, format: {} as Intl.NumberFormatOptions, icon: Check },
  ];

  return (
    <div className="space-y-6">
      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
        {summaryCards.map((card) => (
          <Card key={card.label} className="gap-0">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-xs text-muted-foreground">{card.label}</CardTitle>
              <card.icon className="size-4 text-muted-foreground" />
            </CardHeader>
            <CardPanel className="p-4 py-3 flex flex-col gap-2">
              <NumberFlow value={card.value} format={card.format} className="text-2xl font-semibold text-foreground tabular-nums" />
            </CardPanel>
          </Card>
        ))}
      </div>

      {/* Deals by stage */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Deals by Stage</CardTitle>
        </CardHeader>
        <CardPanel className="space-y-3 p-4">
          {STAGE_ORDER.map((stage) => {
            const count = stageCounts[stage];
            const pct = deals.length > 0 ? Math.round((count / deals.length) * 100) : 0;
            const { label, color } = stageStyles[stage];
            return (
              <div key={stage} className="flex items-center gap-3">
                <span className={cn("text-xs font-medium w-24 shrink-0 px-2 py-0.5 rounded-full text-center", color)}>
                  {label}
                </span>
                <div className="flex-1 h-2 rounded-full bg-muted/50 overflow-hidden">
                  <div
                    className="h-full rounded-full bg-primary transition-all"
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <span className="text-xs font-medium text-foreground tabular-nums w-8 text-right">
                  {count}
                </span>
              </div>
            );
          })}
        </CardPanel>
      </Card>

      {/* Recent activity */}
      <Card className="overflow-hidden">
        <CardHeader>
          <CardTitle className="text-sm">Recent Activity</CardTitle>
        </CardHeader>
        <CardPanel className="px-4 py-4">
          {activity.slice(0, 5).map((a) => (
            <div key={a.id} className="flex items-start gap-3 px-4 py-3 border-b border-border/30 last:border-b-0">
              <Activity className="size-4 text-muted-foreground mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-foreground">{a.action}</div>
                <div className="text-xs text-muted-foreground">{a.detail}</div>
              </div>
              <span className="text-xs text-muted-foreground shrink-0">{a.date}</span>
            </div>
          ))}
          {activity.length === 0 && (
            <div className="px-4 py-8 text-center text-sm text-muted-foreground">No activity yet</div>
          )}
        </CardPanel>
      </Card>
    </div>
  );
}

function DealsTab({
  deals,
  activeEntityId,
  entities,
  dealMap,
}: {
  deals: Deal[];
  activeEntityId: string | null;
  entities: PortfolioEntity[];
  dealMap: Record<string, string[]>;
}) {
  const navigate = useNavigate();
  const isDemo = useIsDemo();
  const { initialDeals: allDeals } = useDealPipelineData();

  const grouped = useMemo(() => {
    if (activeEntityId) return null;
    const groups: { entity: PortfolioEntity; deals: Deal[] }[] = [];
    entities.forEach((entity) => {
      const entityDeals = getEntityDeals(entity.id, dealMap, allDeals);
      if (entityDeals.length > 0) groups.push({ entity, deals: entityDeals });
    });
    return groups;
  }, [activeEntityId, entities, dealMap, allDeals]);

  const renderDealRow = (deal: Deal) => {
    const { label, color } = stageStyles[deal.stage];
    return (
      <button
        key={deal.id}
        onClick={() => navigate(`${isDemo ? PATH_ROOT.demo.deal : PATH_ROOT.main.deal}/${deal.id}`)}
        className="flex items-center gap-3 px-4 py-3 border-b border-border/30 last:border-b-0 hover:bg-muted/30 transition-colors w-full text-left"
      >
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-foreground">{deal.name}</div>
          <div className="text-xs text-muted-foreground">{deal.type}</div>
        </div>
        <span className={cn("text-[10px] font-medium px-2 py-0.5 rounded-full", color)}>
          {label}
        </span>
        <span className="text-sm font-semibold tabular-nums text-foreground w-24 text-right">
          ${deal.value.toLocaleString()}
        </span>
        <div className="w-20 shrink-0">
          <div className="h-1.5 rounded-full bg-muted/50 overflow-hidden">
            <div className="h-full rounded-full bg-primary" style={{ width: `${deal.progress}%` }} />
          </div>
        </div>
        <span className="text-xs text-muted-foreground w-24 text-right shrink-0">
          {deal.dueDate ?? "—"}
        </span>
        <ExternalLink className="size-3.5 text-muted-foreground shrink-0" />
      </button>
    );
  };

  if (grouped) {
    return (
      <div className="space-y-4">
        {grouped.map(({ entity, deals: entityDeals }) => (
          <Card key={entity.id} className="overflow-hidden gap-0">
            <CardHeader className="flex flex-row items-center gap-2 py-3 px-4">
              {entity.image ? (
                <img src={entity.image} alt="" className="size-4 rounded-full object-cover" />
              ) : (
                <entity.icon className={cn("size-4", entity.color)} />
              )}
              <CardTitle className="text-sm mb-0">{entity.name}</CardTitle>
              <span className="text-xs text-muted-foreground ml-auto">{entityDeals.length} deal{entityDeals.length !== 1 ? "s" : ""}</span>
            </CardHeader>
            <CardPanel className="px-4 py-4">{entityDeals.map(renderDealRow)}</CardPanel>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <Card className="overflow-hidden gap-0">
      <CardHeader className="py-3 px-4">
        <CardTitle className="text-sm">Deals</CardTitle>
      </CardHeader>
      <CardPanel className="px-4 py-4">
        {deals.map(renderDealRow)}
        {deals.length === 0 && (
          <div className="px-4 py-8 text-center text-sm text-muted-foreground">No deals</div>
        )}
      </CardPanel>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

const PortfolioPage = () => {
  const {
    entities,
    activeEntityId,
    setActiveEntityId,
    activeEntity: entity,
    dealMap,
    transactions: txMap,
    activity: actMap,
    openCreateDialog,
  } = usePortfolio();
  const { initialDeals: INITIAL_DEALS } = useDealPipelineData();
  const [tab, setTab] = useState(0);

  const deals = useMemo(() => getEntityDeals(activeEntityId, dealMap, INITIAL_DEALS), [activeEntityId, dealMap, INITIAL_DEALS]);
  const transactions = useMemo(() => getEntityTransactions(activeEntityId, txMap), [activeEntityId, txMap]);
  const activity = useMemo(() => getEntityActivity(activeEntityId, actMap), [activeEntityId, actMap]);

  return (
    <div className="h-full flex flex-col">
      <header className="p-3 border-b border-border sticky top-0 bg-background z-10 flex items-center justify-between">
        <div>
          <h1 className="font-semibold">Portfolio</h1>
          <p className="text-muted-foreground text-sm">
            Manage your portfolio entities — artists, brands, and IP
          </p>
        </div>
        <Button size="sm" onClick={openCreateDialog}>
          <Plus className="size-4 mr-1" />
          New Entity
        </Button>
      </header>

      <div className="flex overflow-hidden">
        <EntitySidebar entities={entities} activeId={activeEntityId} onSelect={setActiveEntityId} />

        <div className="flex-1 overflow-y-auto">
          {/* Entity header */}
          <div className="p-4 border-b border-border flex items-center gap-3">
            {entity ? (
              <>
                <div className={cn("size-10 rounded-full grid place-items-center overflow-hidden", !entity.image && entity.bgClass)}>
                  {entity.image ? (
                    <img src={entity.image} alt="" className="size-full object-cover" />
                  ) : (
                    <entity.icon className={cn("size-5", entity.color)} />
                  )}
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-foreground">{entity.name}</h2>
                  <span className="text-xs text-muted-foreground capitalize">{entity.type} · {entity.activeDeals} active deals · {entity.totalValue}</span>
                </div>
              </>
            ) : (
              <>
                <div className="size-10 rounded-full grid place-items-center bg-primary/10">
                  <Briefcase className="size-5 text-primary" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-foreground">All Portfolios</h2>
                  <span className="text-xs text-muted-foreground">{INITIAL_DEALS.length} total deals · Aggregate overview</span>
                </div>
              </>
            )}
          </div>

          {/* Tabs */}
          <div className="p-4">
            <Tabs value={tab} onValueChange={setTab}>
              <TabsList className="mb-4">
                <TabsTrigger value={0}>Overview</TabsTrigger>
                <TabsTrigger value={1}>Deals</TabsTrigger>
                <TabsTrigger value={2}>Financials</TabsTrigger>
              </TabsList>

              <TabsPanel value={0}>
                <OverviewTab deals={deals} transactions={transactions} activity={activity} entity={entity} />
              </TabsPanel>
              <TabsPanel value={1}>
                <DealsTab deals={deals} activeEntityId={activeEntityId} entities={entities} dealMap={dealMap} />
              </TabsPanel>
              <TabsPanel value={2}>
                <FinancialsTab transactions={transactions} entity={entity} deals={deals} dealMap={dealMap} />
              </TabsPanel>
            </Tabs>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PortfolioPage;
